import React, { useEffect, useState, useRef } from 'react';
import styled from 'styled-components';
import { Link, useHistory } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useProductsContext } from '../../context/products_context';
import { useLanguageContext } from '../../context/language_context';
import { useUserContext } from '../../context/user_context';
import { formatPrice, getSecureUrl } from '../../utils/helpers';
import { categories_url, hero_images_url, platform_reviews_url } from '../../utils/constants';
import Loading from '../../components/Loading';
import Error from '../../components/Error';

const HomePage = () => {
  const { t, isArabic } = useLanguageContext();
  const { currentUser } = useUserContext();
  const history = useHistory();
  const {
    products_loading: loading,
    products_error: error,
    featured_products: featured,
    products,
  } = useProductsContext();

  const [categories, setCategories] = useState([]);
  const [heroImages, setHeroImages] = useState([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [platformReviews, setPlatformReviews] = useState([]);
  const slideInterval = useRef(null);

  // Review Modal State
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [reviewStars, setReviewStars] = useState(0);
  const [reviewComment, setReviewComment] = useState('');
  const [reviewLoading, setReviewLoading] = useState(false);

  // Fetch platform reviews
  const fetchPlatformReviews = async () => {
    try {
      const { data } = await axios.get(platform_reviews_url);
      if (data.success) {
        const reviews = (data.reviews || []).map((r) => {
          const sanitized = { ...r };
          Object.keys(sanitized).forEach((k) => {
            if (typeof sanitized[k] === 'string' && sanitized[k].startsWith('http://')) {
              sanitized[k] = getSecureUrl(sanitized[k]);
            }
          });
          return sanitized;
        });
        setPlatformReviews(reviews);
      }
    } catch (error) {
      console.error('Error fetching platform reviews:', error);
    }
  };

  // Handle Add Review Button Click
  const handleAddReviewClick = () => {
    if (!currentUser) {
      history.push('/login');
    } else {
      setShowReviewModal(true);
    }
  };

  // Handle Review Submit
  const handleReviewSubmit = async () => {
    if (reviewStars === 0) {
      toast.error(isArabic ? 'يرجى تحديد تقييم' : 'Please select a rating');
      return;
    }
    if (!reviewComment.trim()) {
      toast.error(isArabic ? 'يرجى كتابة تعليق' : 'Please write a comment');
      return;
    }

    setReviewLoading(true);
    try {
      const { data } = await axios.post(platform_reviews_url, {
        name: currentUser.displayName || 'User',
        email: currentUser.email,
        rating: reviewStars,
        comment: reviewComment,
      });
      
      if (data.success) {
        toast.success(isArabic ? 'تم إضافة تقييمك بنجاح!' : 'Your review has been added successfully!');
        setShowReviewModal(false);
        setReviewStars(0);
        setReviewComment('');
        // Refresh reviews
        fetchPlatformReviews();
      } else {
        toast.error(data.message || (isArabic ? 'حدث خطأ' : 'An error occurred'));
      }
    } catch (error) {
      toast.error(isArabic ? 'حدث خطأ أثناء إرسال التقييم' : 'Error submitting review');
    }
    setReviewLoading(false);
  };

  // Default hero image if none in database
  const defaultHeroImage = {
    image: { url: 'https://lh3.googleusercontent.com/aida-public/AB6AXuBwUFGqpBAvHabWlHtZEuSEB2mknahseKzNrPlKwE7BwxZLIDXxLROOaCCD_lMKWwbJFggN9N85Rwl2Y00daeehseGNT73LTHBSP7oboxb5TJCBRI0MBM-n_LZOJ5daRqC1_smB75E1tmCkk-BHQe0v5xgKXyxF6fRwFdbzrlozywnC9hBhaX3k225UqD0WcKqpDY8Cv-4Ibfuo41wk8CxqARoLTeHXPFpzqPUdPidGejB9X4zbqoAedRXZ-efS6JO_g3kggyTc2i0' },
    title: 'Redefine Your Living Space',
    titleAr: 'صمم مساحتك المريحة',
    subtitle: 'Premium products crafted for comfort, style, and the modern home.',
    subtitleAr: 'منتجات فاخرة مصممة للراحة والأناقة والمنزل العصري.',
  };

  useEffect(() => {
    document.title = 'Art & Bois | Home';
    fetchCategories();
    fetchHeroImages();
    fetchPlatformReviews();
  }, []);

  // Auto-slide carousel - faster transition (3 seconds)
  useEffect(() => {
    if (heroImages.length > 1) {
      slideInterval.current = setInterval(() => {
        setCurrentSlide((prev) => (prev + 1) % heroImages.length);
      }, 3000);
    }
    return () => {
      if (slideInterval.current) {
        clearInterval(slideInterval.current);
      }
    };
  }, [heroImages.length]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(categories_url);
      const cats = (response.data.data || []).map((cat) => ({
        ...cat,
        image: cat.image ? { ...cat.image, url: getSecureUrl(cat.image.url) } : cat.image,
      }));
      setCategories(cats);
    } catch (error) {
      // Silent fail
    }
  };

  const fetchHeroImages = async () => {
    try {
      const response = await axios.get(hero_images_url);
      const imgs = (response.data.data || []).map((h) => ({
        ...h,
        image: h.image ? { ...h.image, url: getSecureUrl(h.image.url) } : h.image,
      }));
      setHeroImages(imgs);
    } catch (error) {
      // Silent fail - will use default
    }
  };

  const goToSlide = (index) => {
    setCurrentSlide(index);
    // Reset interval
    if (slideInterval.current) {
      clearInterval(slideInterval.current);
      slideInterval.current = setInterval(() => {
        setCurrentSlide((prev) => (prev + 1) % heroImages.length);
      }, 3000);
    }
  };

  const currentHero = heroImages.length > 0 ? heroImages[currentSlide] : defaultHeroImage;

  if (loading) {
    return <Loading />;
  }

  if (error) {
    return <Error />;
  }

  // Get unique categories from products if no categories in DB
  const displayCategories = categories.length > 0 
    ? categories 
    : [...new Set(products.map(p => p.category))].slice(0, 4).map((cat, index) => ({
        _id: index,
        name: cat,
        image: { url: products.find(p => p.category === cat)?.images?.[0]?.url || '' }
      }));

  return (
    <Wrapper dir={isArabic ? 'rtl' : 'ltr'}>
      {/* Hero Section with Carousel */}
      <section className="hero-section">
        <div 
          className="hero-background"
          style={{ backgroundImage: `url(${currentHero.image?.url || currentHero.image})` }}
        />
        <div className="hero-overlay"></div>
        <div className="hero-content">
          <h1 className="hero-title">
            {isArabic 
              ? (currentHero.titleAr || 'صمم مساحتك المريحة')
              : (currentHero.title || 'Redefine Your Living Space')
            }
          </h1>
          <p className="hero-subtitle">
            {isArabic
              ? (currentHero.subtitleAr || 'منتجات فاخرة مصممة للراحة والأناقة والمنزل العصري.')
              : (currentHero.subtitle || 'Premium products crafted for comfort, style, and the modern home.')
            }
          </p>
          <div className="hero-buttons">
            <Link to="/products" className="hero-btn-primary">
              {t.hero.shopNow}
            </Link>
          </div>
        </div>
        
        {/* Carousel Dots */}
        {heroImages.length > 1 && (
          <div className="carousel-dots">
            {heroImages.map((_, index) => (
              <button
                key={index}
                className={`carousel-dot ${index === currentSlide ? 'active' : ''}`}
                onClick={() => goToSlide(index)}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>
        )}
      </section>

      {/* Categories Section */}
      {displayCategories.length > 0 && (
        <section className="categories-section">
          <div className="categories-header">
            <div>
              <span className="categories-label">{isArabic ? 'تصفح' : 'Browse'}</span>
              <h2 className="categories-title">
                {isArabic ? 'تسوق حسب الفئة' : 'Shop by Category'}
              </h2>
            </div>
            <Link to="/products" className="categories-link">
              {isArabic ? 'عرض جميع الفئات' : 'View All Categories'}
            </Link>
          </div>
          <div className="categories-grid">
            {displayCategories.slice(0, 4).map((category) => (
              <Link 
                to={`/products?category=${category.name}`} 
                key={category._id} 
                className="category-card"
              >
                <div
                  className="category-image"
                  style={{
                    backgroundImage: `url(${category.image?.url || ''})`,
                  }}
                ></div>
                <div className="category-overlay"></div>
                <h3 className="category-name">
                  {category.name.charAt(0).toUpperCase() + category.name.slice(1)}
                </h3>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Featured Products Section */}
      <section className="products-section">
        <h2 className="products-title">{t.products.featured}</h2>
        <div className="products-grid">
          {featured.slice(0, 4).map((product) => {
            const { id, name, price, images, image } = product;
            // Handle different image structures
            const imageUrl = image || images?.[0]?.url || images?.[0] || '';
            return (
              <Link to={`/products/${id}`} key={id} className="product-card">
                <div className="product-image-container">
                  <img
                    className="product-image"
                    src={imageUrl}
                    alt={name}
                    referrerPolicy="no-referrer"
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                  <button className="add-to-cart-btn">
                    {t.products.addToCart}
                  </button>
                </div>
                <div className="product-info">
                  <h3 className="product-name">{name}</h3>
                  <p className="product-price">{formatPrice(price)}</p>
                </div>
              </Link>
            );
          })}
        </div>
        <div className="products-cta">
          <Link to="/products" className="view-all-btn">
            {t.products.allProducts}
          </Link>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="features-section">
        <div className="features-grid">
          <div className="feature-card">
            <div className="feature-icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 2L2 7l10 5 10-5-10-5z"></path>
                <path d="M2 17l10 5 10-5"></path>
                <path d="M2 12l10 5 10-5"></path>
              </svg>
            </div>
            <h3 className="feature-title">
              {isArabic ? 'منتجات أصلية' : 'Authentic Products'}
            </h3>
            <p className="feature-text">
              {isArabic
                ? 'منتجات أصلية 100% بجودة مضمونة وشهادات موثقة.'
                : '100% authentic products with guaranteed quality.'}
            </p>
          </div>
          <div className="feature-card">
            <div className="feature-icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="1" y="3" width="15" height="13"></rect>
                <polygon points="16 8 20 8 23 11 23 16 16 16 16 8"></polygon>
                <circle cx="5.5" cy="18.5" r="2.5"></circle>
                <circle cx="18.5" cy="18.5" r="2.5"></circle>
              </svg>
            </div>
            <h3 className="feature-title">
              {isArabic ? 'توصيل مجاني' : 'Free Shipping'}
            </h3>
            <p className="feature-text">
              {isArabic
                ? 'توصيل مجاني لجميع الطلبات، مباشرة إلى باب منزلك.'
                : 'Free shipping on all orders, delivered to your doorstep.'}
            </p>
          </div>
          <div className="feature-card">
            <div className="feature-icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                <polyline points="22 4 12 14.01 9 11.01"></polyline>
              </svg>
            </div>
            <h3 className="feature-title">
              {isArabic ? 'ضمان الجودة' : 'Quality Guarantee'}
            </h3>
            <p className="feature-text">
              {isArabic
                ? 'جودة يمكنك الوثوق بها، مدعومة بضماننا الشامل.'
                : 'Quality you can trust, backed by our warranty.'}
            </p>
          </div>
          <div className="feature-card">
            <div className="feature-icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
              </svg>
            </div>
            <h3 className="feature-title">
              {isArabic ? 'دعم العملاء' : 'Customer Support'}
            </h3>
            <p className="feature-text">
              {isArabic
                ? 'فريق دعم متخصص جاهز لمساعدتك على مدار الساعة.'
                : 'Dedicated support team ready to assist you.'}
            </p>
          </div>
        </div>
      </section>

      {/* Reviews Section */}
      <section className="reviews-section">
        <h2 className="reviews-title">
          {isArabic ? 'آراء العملاء' : 'Customer Reviews'}
        </h2>
        {platformReviews.length > 0 ? (
          <>
            <div className="reviews-grid">
              {platformReviews.slice(0, 3).map((review, index) => (
                <div key={review._id || index} className="review-card">
                  <div className="review-header">
                    <div className="review-avatar">
                      {review.name.charAt(0).toUpperCase()}
                    </div>
                    <div className="review-info">
                      <div className="review-name-row">
                        <h4 className="review-name">{review.name}</h4>
                        <span className="verified-badge">
                          <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z"/>
                          </svg>
                          {isArabic ? 'مشتري موثق' : 'Verified Buyer'}
                        </span>
                      </div>
                      <div className="review-stars">
                        {[...Array(5)].map((_, i) => (
                          <span key={i} className={i < review.rating ? 'star filled' : 'star'}>
                            ★
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                  <p className="review-comment">"{review.comment}"</p>
                </div>
              ))}
            </div>
            <div className="add-review-container">
              <button 
                className="add-review-btn"
                onClick={handleAddReviewClick}
              >
                {isArabic ? 'أضف تقييمك' : 'Add Your Review'}
              </button>
            </div>
          </>
        ) : (
          <div className="no-reviews">
            <div className="no-reviews-icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
              </svg>
            </div>
            <p className="no-reviews-text">
              {isArabic 
                ? 'لا توجد تقييمات حتى الآن. كن أول من يشاركنا رأيه!' 
                : 'No reviews yet. Be the first to share your experience!'}
            </p>
            <button 
              className="no-reviews-btn"
              onClick={handleAddReviewClick}
            >
              {isArabic ? 'أضف تقييم' : 'Add Review'}
            </button>
          </div>
        )}
      </section>

      {/* Review Modal */}
      {showReviewModal && (
        <div className="review-modal-overlay" onClick={() => setShowReviewModal(false)}>
          <div className="review-modal" onClick={(e) => e.stopPropagation()} style={{ direction: isArabic ? 'rtl' : 'ltr' }}>
            <button className="modal-close" onClick={() => setShowReviewModal(false)}>×</button>
            <h3 className="modal-title">{isArabic ? 'أضف تقييمك' : 'Add Your Review'}</h3>
            
            <div className="modal-field">
              <label>{isArabic ? 'التقييم' : 'Rating'}</label>
              <div className="modal-stars">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    type="button"
                    className={`star-btn ${star <= reviewStars ? 'active' : ''}`}
                    onClick={() => setReviewStars(star)}
                  >
                    ★
                  </button>
                ))}
              </div>
            </div>

            <div className="modal-field">
              <label>{isArabic ? 'تعليقك' : 'Your Comment'}</label>
              <textarea
                value={reviewComment}
                onChange={(e) => setReviewComment(e.target.value)}
                placeholder={isArabic ? 'شاركنا تجربتك مع متجرنا...' : 'Share your experience with our store...'}
                className="modal-textarea"
                rows={4}
              />
            </div>

            <div className="modal-actions">
              <button 
                className="modal-submit"
                onClick={handleReviewSubmit}
                disabled={reviewLoading}
              >
                {reviewLoading 
                  ? (isArabic ? 'جاري الإرسال...' : 'Submitting...') 
                  : (isArabic ? 'إرسال التقييم' : 'Submit Review')}
              </button>
              <button 
                className="modal-cancel"
                onClick={() => setShowReviewModal(false)}
              >
                {isArabic ? 'إلغاء' : 'Cancel'}
              </button>
            </div>
          </div>
        </div>
      )}
    </Wrapper>
  );
};

const Wrapper = styled.main`
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;700;900&family=Manrope:wght@400;500;600;700&display=swap');

  font-family: 'Manrope', sans-serif;
  background: #f6f8f8;

  /* Keyframe Animations */
  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(30px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes slideInLeft {
    from {
      opacity: 0;
      transform: translateX(-30px);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }

  @keyframes zoomIn {
    from {
      transform: scale(1.1);
    }
    to {
      transform: scale(1);
    }
  }

  @keyframes pulse {
    0%, 100% {
      transform: scale(1);
    }
    50% {
      transform: scale(1.05);
    }
  }

  /* Hero Section with Carousel - Mobile First */
  .hero-section {
    position: relative;
    width: 100%;
    min-height: calc(100vh - 70px);
    min-height: calc(100svh - 70px); /* Small viewport height for mobile browsers */
    margin-top: -70px;
    padding-top: 70px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding-left: 1rem;
    padding-right: 1rem;
    padding-bottom: 1rem;
    overflow: hidden;
  }

  @media (min-width: 768px) {
    .hero-section {
      min-height: calc(100vh - 70px);
      justify-content: flex-start;
      padding-left: 2rem;
      padding-right: 2rem;
    }
  }

  @media (min-width: 1024px) {
    .hero-section {
      min-height: calc(100vh - 70px);
      padding-left: 5rem;
      padding-right: 5rem;
    }
  }

  .hero-background {
    position: absolute;
    inset: 0;
    background-size: cover;
    background-position: center;
    animation: zoomIn 6s ease-out forwards;
    transition: background-image 0.5s ease-in-out;
  }

  .hero-overlay {
    position: absolute;
    inset: 0;
    background: linear-gradient(
      to bottom,
      rgba(0, 0, 0, 0.4) 0%,
      rgba(0, 0, 0, 0.5) 50%,
      rgba(0, 0, 0, 0.6) 100%
    );
  }

  @media (min-width: 768px) {
    .hero-overlay {
      background: linear-gradient(
        to right,
        rgba(0, 0, 0, 0.6) 0%,
        rgba(0, 0, 0, 0.4) 40%,
        rgba(0, 0, 0, 0.2) 70%,
        transparent 100%
      );
    }
  }

  .hero-content {
    position: relative;
    z-index: 10;
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    gap: 1rem;
    max-width: 100%;
    padding: 2rem 0;
    animation: fadeInUp 0.8s ease-out;
  }

  @media (min-width: 768px) {
    .hero-content {
      align-items: flex-start;
      text-align: left;
      gap: 1.5rem;
      max-width: 550px;
      padding-top: 3rem;
    }
  }

  @media (min-width: 1024px) {
    .hero-content {
      max-width: 600px;
      padding-top: 5rem;
    }
  }

  .hero-title {
    font-family: 'Playfair Display', serif;
    font-size: 2rem;
    font-weight: 900;
    line-height: 1.15;
    letter-spacing: -0.02em;
    color: white;
    text-shadow: 0 2px 8px rgba(0, 0, 0, 0.4);
    margin: 0;
    animation: slideInLeft 0.8s ease-out 0.2s both;
  }

  @media (min-width: 480px) {
    .hero-title {
      font-size: 2.5rem;
    }
  }

  @media (min-width: 768px) {
    .hero-title {
      font-size: 3rem;
    }
  }

  @media (min-width: 1024px) {
    .hero-title {
      font-size: 4rem;
    }
  }

  @media (min-width: 1280px) {
    .hero-title {
      font-size: 4.5rem;
    }
  }

  .hero-subtitle {
    font-size: 0.9rem;
    font-weight: 400;
    line-height: 1.7;
    color: rgba(255, 255, 255, 0.95);
    margin: 0;
    max-width: 90%;
    animation: slideInLeft 0.8s ease-out 0.4s both;
  }

  @media (min-width: 480px) {
    .hero-subtitle {
      font-size: 1rem;
    }
  }

  @media (min-width: 768px) {
    .hero-subtitle {
      font-size: 1.1rem;
      max-width: 100%;
    }
  }

  @media (min-width: 1024px) {
    .hero-subtitle {
      font-size: 1.25rem;
    }
  }

  .hero-buttons {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 1rem;
    margin-top: 0.5rem;
    animation: fadeInUp 0.8s ease-out 0.6s both;
  }

  @media (min-width: 768px) {
    .hero-buttons {
      justify-content: flex-start;
      margin-top: 1rem;
    }
  }

  .hero-btn-primary {
    display: flex;
    height: 48px;
    min-width: 140px;
    cursor: pointer;
    align-items: center;
    justify-content: center;
    border-radius: 0.5rem;
    background: #FF5252;
    padding: 0 1.5rem;
    font-size: 0.9rem;
    font-weight: 700;
    color: white;
    border: none;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    text-decoration: none;
    box-shadow: 0 4px 15px rgba(255, 82, 82, 0.3);
  }

  .hero-btn-primary:hover {
    background: #e04848;
    box-shadow: 0 8px 25px rgba(255, 82, 82, 0.4);
    transform: translateY(-3px);
  }

  .hero-btn-primary:active {
    transform: translateY(-1px);
  }

  @media (min-width: 768px) {
    .hero-btn-primary {
      height: 52px;
      min-width: 160px;
      padding: 0 2rem;
      font-size: 1rem;
    }
  }

  /* Carousel Dots - Mobile Optimized */
  .carousel-dots {
    position: absolute;
    bottom: 1.5rem;
    left: 50%;
    transform: translateX(-50%);
    display: flex;
    gap: 0.5rem;
    z-index: 20;
    animation: fadeIn 0.8s ease-out 0.8s both;
  }

  @media (min-width: 768px) {
    .carousel-dots {
      bottom: 2rem;
      gap: 0.75rem;
    }
  }

  .carousel-dot {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    border: 2px solid white;
    background: transparent;
    cursor: pointer;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    padding: 0;
  }

  .carousel-dot.active {
    background: white;
    transform: scale(1.3);
    box-shadow: 0 0 10px rgba(255, 255, 255, 0.5);
  }

  .carousel-dot:hover {
    background: rgba(255, 255, 255, 0.6);
    transform: scale(1.1);
  }

  @media (min-width: 768px) {
    .carousel-dot {
      width: 12px;
      height: 12px;
    }
  }

  /* Categories Section */
  .categories-section {
    max-width: 1440px;
    margin: 0 auto;
    padding: 3rem 1rem;
  }

  @media (min-width: 768px) {
    .categories-section {
      padding: 4rem 1.5rem;
    }
  }

  @media (min-width: 1024px) {
    .categories-section {
      padding: 5rem;
    }
  }

  .categories-header {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
    margin-bottom: 2rem;
  }

  @media (min-width: 640px) {
    .categories-header {
      flex-direction: row;
      align-items: flex-end;
      justify-content: space-between;
      margin-bottom: 3rem;
    }
  }

  .categories-label {
    display: block;
    margin-bottom: 0.25rem;
    font-size: 0.75rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    color: #17bfcf;
  }

  @media (min-width: 768px) {
    .categories-label {
      font-size: 0.875rem;
      margin-bottom: 0.5rem;
    }
  }

  .categories-title {
    font-family: 'Playfair Display', serif;
    font-size: 1.5rem;
    font-weight: 700;
    color: #111718;
    margin: 0;
  }

  @media (min-width: 768px) {
    .categories-title {
      font-size: 2.25rem;
    }
  }

  .categories-link {
    font-size: 0.8rem;
    font-weight: 700;
    color: #111718;
    text-decoration: underline;
    text-decoration-thickness: 2px;
    text-underline-offset: 4px;
    transition: color 0.3s ease;
  }

  .categories-link:hover {
    color: #17bfcf;
  }

  @media (min-width: 640px) {
    .categories-link {
      font-size: 0.875rem;
    }
  }

  .categories-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 1rem;
  }

  @media (min-width: 768px) {
    .categories-grid {
      gap: 1.5rem;
    }
  }

  @media (min-width: 1024px) {
    .categories-grid {
      grid-template-columns: repeat(4, 1fr);
    }
  }

  .category-card {
    position: relative;
    padding-bottom: 133.33%; /* aspect-ratio: 3/4 fallback */
    height: 0;
    overflow: hidden;
    border-radius: 0.75rem;
    background: #e0e0e0;
    display: block;
    text-decoration: none;
    transition: transform 0.3s ease, box-shadow 0.3s ease;
  }

  .category-card:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
  }

  .category-image {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-size: cover;
    background-position: center;
    transition: transform 0.7s cubic-bezier(0.4, 0, 0.2, 1);
  }

  .category-card:hover .category-image {
    transform: scale(1.08);
  }

  .category-overlay {
    position: absolute;
    inset: 0;
    background: linear-gradient(to top, rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.1) 50%, transparent);
    opacity: 0.8;
    transition: opacity 0.3s ease;
  }

  .category-card:hover .category-overlay {
    opacity: 0.9;
  }

  .category-name {
    position: absolute;
    bottom: 1rem;
    left: 1rem;
    right: 1rem;
    font-family: 'Playfair Display', serif;
    font-size: 1rem;
    color: white;
    margin: 0;
    text-transform: capitalize;
  }

  @media (min-width: 768px) {
    .category-name {
      bottom: 1.5rem;
      left: 1.5rem;
      font-size: 1.5rem;
    }
  }

  /* Products Section */
  .products-section {
    max-width: 1440px;
    margin: 0 auto;
    padding: 0 1rem 3rem;
  }

  @media (min-width: 768px) {
    .products-section {
      padding: 0 1.5rem 4rem;
    }
  }

  @media (min-width: 1024px) {
    .products-section {
      padding: 0 5rem 5rem;
    }
  }

  .products-title {
    font-family: 'Playfair Display', serif;
    font-size: 1.5rem;
    font-weight: 700;
    color: #111718;
    margin-bottom: 1.5rem;
  }

  @media (min-width: 768px) {
    .products-title {
      font-size: 2.25rem;
      margin-bottom: 2.5rem;
    }
  }

  .products-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 1rem;
  }

  @media (min-width: 768px) {
    .products-grid {
      gap: 1.5rem;
    }
  }

  @media (min-width: 1024px) {
    .products-grid {
      grid-template-columns: repeat(4, 1fr);
    }
  }

  .product-card {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    text-decoration: none;
    transition: transform 0.3s ease;
  }

  .product-card:hover {
    transform: translateY(-5px);
  }

  @media (min-width: 768px) {
    .product-card {
      gap: 0.75rem;
    }
  }

  .product-image-container {
    position: relative;
    padding-bottom: 125%; /* aspect-ratio: 4/5 fallback */
    height: 0;
    width: 100%;
    overflow: hidden;
    border-radius: 0.5rem;
    background: #f0f0f0;
  }

  .product-image {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center;
    transition: transform 0.5s cubic-bezier(0.4, 0, 0.2, 1);
  }

  .product-card:hover .product-image {
    transform: scale(1.08);
  }

  .add-to-cart-btn {
    position: absolute;
    bottom: 0.75rem;
    left: 0.5rem;
    right: 0.5rem;
    padding: 0.5rem;
    background: white;
    border: none;
    border-radius: 0.25rem;
    font-size: 0.7rem;
    font-weight: 700;
    color: #111718;
    cursor: pointer;
    opacity: 0;
    transform: translateY(1rem);
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }

  @media (min-width: 768px) {
    .add-to-cart-btn {
      bottom: 1rem;
      left: 1rem;
      right: 1rem;
      padding: 0.75rem;
      font-size: 0.875rem;
    }
  }

  .product-card:hover .add-to-cart-btn {
    opacity: 1;
    transform: translateY(0);
  }

  .add-to-cart-btn:hover {
    background: #17bfcf;
    color: white;
  }

  .product-info {
    padding: 0.25rem 0;
  }

  .product-name {
    font-size: 0.875rem;
    font-weight: 500;
    color: #111718;
    margin: 0 0 0.25rem;
    line-height: 1.4;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  @media (min-width: 768px) {
    .product-name {
      font-size: 1.125rem;
    }
  }

  .product-price {
    font-size: 0.875rem;
    font-weight: 700;
    color: #6b7280;
    margin: 0;
  }

  @media (min-width: 768px) {
    .product-price {
      font-size: 1rem;
    }
  }

  .products-cta {
    text-align: center;
    margin-top: 2rem;
  }

  @media (min-width: 768px) {
    .products-cta {
      margin-top: 3rem;
    }
  }

  .view-all-btn {
    display: inline-flex;
    height: 44px;
    min-width: 150px;
    cursor: pointer;
    align-items: center;
    justify-content: center;
    border-radius: 0.5rem;
    background: #FF5252;
    padding: 0 1.5rem;
    font-size: 0.9rem;
    font-weight: 700;
    color: white;
    text-decoration: none;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    box-shadow: 0 4px 15px rgba(255, 82, 82, 0.2);
  }

  @media (min-width: 768px) {
    .view-all-btn {
      height: 48px;
      min-width: 180px;
      padding: 0 2rem;
      font-size: 1rem;
    }
  }

  .view-all-btn:hover {
    background: #e04848;
    box-shadow: 0 10px 25px rgba(255, 82, 82, 0.3);
    transform: translateY(-3px);
  }

  /* Features Section */
  .features-section {
    width: 100%;
    background: #f0f4f4;
    padding: 3rem 1rem;
  }

  @media (min-width: 768px) {
    .features-section {
      padding: 4rem 1.5rem;
    }
  }

  @media (min-width: 1024px) {
    .features-section {
      padding: 5rem;
    }
  }

  .features-grid {
    max-width: 1440px;
    margin: 0 auto;
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 1.5rem;
  }

  @media (min-width: 768px) {
    .features-grid {
      gap: 2rem;
    }
  }

  @media (min-width: 1024px) {
    .features-grid {
      grid-template-columns: repeat(4, 1fr);
      gap: 3rem;
    }
  }

  .feature-card {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    gap: 0.75rem;
    padding: 1rem;
    background: white;
    border-radius: 0.75rem;
    transition: transform 0.3s ease, box-shadow 0.3s ease;
  }

  .feature-card:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  }

  @media (min-width: 768px) {
    .feature-card {
      align-items: flex-start;
      text-align: left;
      gap: 1rem;
      padding: 0;
      background: transparent;
      border-radius: 0;
    }
    
    .feature-card:hover {
      transform: none;
      box-shadow: none;
    }
  }

  .feature-icon {
    display: flex;
    width: 40px;
    height: 40px;
    align-items: center;
    justify-content: center;
    border-radius: 0.5rem;
    background: white;
    color: #17bfcf;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  }

  @media (min-width: 768px) {
    .feature-icon {
      width: 48px;
      height: 48px;
    }
  }

  .feature-title {
    font-family: 'Playfair Display', serif;
    font-size: 1rem;
    font-weight: 700;
    color: #111718;
    margin: 0;
  }

  @media (min-width: 768px) {
    .feature-title {
      font-size: 1.25rem;
    }
  }

  .feature-text {
    font-size: 0.75rem;
    line-height: 1.6;
    color: #6b7280;
    margin: 0;
    display: none;
  }

  @media (min-width: 768px) {
    .feature-text {
      display: block;
      font-size: 0.875rem;
    }
  }

  /* Reviews Section */
  .reviews-section {
    max-width: 1440px;
    margin: 0 auto;
    padding: 3rem 1rem;
  }

  @media (min-width: 640px) {
    .reviews-section {
      padding: 4rem 1.5rem;
    }
  }

  @media (min-width: 1024px) {
    .reviews-section {
      padding: 5rem;
    }
  }

  .reviews-title {
    font-family: 'Playfair Display', serif;
    font-size: 1.5rem;
    font-weight: 700;
    color: #111718;
    margin-bottom: 1.5rem;
    text-align: center;
  }

  @media (min-width: 640px) {
    .reviews-title {
      font-size: 1.875rem;
      margin-bottom: 2rem;
    }
  }

  @media (min-width: 768px) {
    .reviews-title {
      font-size: 2.25rem;
      margin-bottom: 2.5rem;
    }
  }

  .reviews-grid {
    display: grid;
    grid-template-columns: 1fr;
    gap: 1rem;
  }

  @media (min-width: 640px) {
    .reviews-grid {
      grid-template-columns: repeat(2, 1fr);
      gap: 1.25rem;
    }
  }

  @media (min-width: 1024px) {
    .reviews-grid {
      grid-template-columns: repeat(3, 1fr);
      gap: 1.5rem;
    }
  }

  .review-card {
    background: white;
    border-radius: 0.75rem;
    padding: 1.25rem;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    transition: transform 0.3s ease, box-shadow 0.3s ease;
  }

  .review-card:hover {
    transform: translateY(-3px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.12);
  }

  @media (min-width: 768px) {
    .review-card {
      padding: 1.5rem;
    }
  }

  .review-header {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    margin-bottom: 0.75rem;
  }

  @media (min-width: 768px) {
    .review-header {
      gap: 1rem;
      margin-bottom: 1rem;
    }
  }

  .review-avatar {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background: linear-gradient(135deg, #17bfcf, #0d9488);
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-weight: 700;
    font-size: 1rem;
    flex-shrink: 0;
  }

  @media (min-width: 768px) {
    .review-avatar {
      width: 48px;
      height: 48px;
      font-size: 1.25rem;
    }
  }

  .review-info {
    flex: 1;
    min-width: 0;
  }

  .review-name-row {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    flex-wrap: wrap;
    margin-bottom: 0.25rem;
  }

  .review-name {
    font-weight: 600;
    color: #111718;
    margin: 0;
    font-size: 0.9375rem;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .verified-badge {
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    background: linear-gradient(135deg, #10b981 0%, #059669 100%);
    color: white;
    font-size: 0.6875rem;
    font-weight: 600;
    padding: 0.125rem 0.5rem;
    border-radius: 1rem;
    white-space: nowrap;
  }

  .verified-badge svg {
    flex-shrink: 0;
  }

  @media (min-width: 768px) {
    .review-name {
      font-size: 1rem;
    }
  }

  .review-stars {
    display: flex;
    gap: 2px;
  }

  .star {
    color: #d1d5db;
    font-size: 0.875rem;
  }

  .star.filled {
    color: #fbbf24;
  }

  @media (min-width: 768px) {
    .star {
      font-size: 1rem;
    }
  }

  .review-comment {
    font-size: 0.875rem;
    line-height: 1.6;
    color: #4b5563;
    margin: 0 0 0.75rem;
    font-style: italic;
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  @media (min-width: 768px) {
    .review-comment {
      font-size: 0.9375rem;
      margin: 0 0 1rem;
      -webkit-line-clamp: none;
      overflow: visible;
    }
  }

  .review-product {
    font-size: 0.75rem;
    color: #17bfcf;
    text-decoration: none;
    display: inline-block;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .review-product:hover {
    text-decoration: underline;
  }

  .no-reviews {
    text-align: center;
    padding: 2rem 1rem;
    background: white;
    border-radius: 0.75rem;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  }

  @media (min-width: 768px) {
    .no-reviews {
      padding: 3rem 1rem;
    }
  }

  .no-reviews-icon {
    color: #d1d5db;
    margin-bottom: 0.75rem;
  }

  @media (min-width: 768px) {
    .no-reviews-icon {
      margin-bottom: 1rem;
    }
  }

  .no-reviews-text {
    color: #6b7280;
    margin-bottom: 1rem;
    font-size: 0.9375rem;
  }

  @media (min-width: 768px) {
    .no-reviews-text {
      margin-bottom: 1.5rem;
      font-size: 1rem;
    }
  }

  .no-reviews-btn {
    display: inline-flex;
    height: 40px;
    padding: 0 1.25rem;
    align-items: center;
    justify-content: center;
    background: #17bfcf;
    color: white;
    border-radius: 0.5rem;
    font-weight: 600;
    font-size: 0.9375rem;
    text-decoration: none;
    transition: all 0.3s ease;
  }

  .no-reviews-btn:hover {
    background: #0d9488;
    transform: translateY(-2px);
  }

  @media (min-width: 768px) {
    .no-reviews-btn {
      padding: 0 1.5rem;
    }
  }

  /* Add Review Button Container */
  .add-review-container {
    display: flex;
    justify-content: center;
    margin-top: 2rem;
  }

  .add-review-btn {
    display: inline-flex;
    height: 48px;
    padding: 0 2rem;
    align-items: center;
    justify-content: center;
    background: linear-gradient(135deg, #17bfcf 0%, #0d9488 100%);
    color: white;
    border: none;
    border-radius: 0.75rem;
    font-weight: 600;
    font-size: 1rem;
    cursor: pointer;
    transition: all 0.3s ease;
    box-shadow: 0 4px 15px rgba(23, 191, 207, 0.3);
  }

  .add-review-btn:hover {
    transform: translateY(-3px);
    box-shadow: 0 6px 20px rgba(23, 191, 207, 0.4);
  }

  @media (min-width: 768px) {
    .add-review-container {
      margin-top: 3rem;
    }
    
    .add-review-btn {
      height: 52px;
      padding: 0 2.5rem;
      font-size: 1.0625rem;
    }
  }

  /* Review Modal Styles */
  .review-modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.6);
    backdrop-filter: blur(4px);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
    padding: 1rem;
  }

  .review-modal {
    background: white;
    border-radius: 1rem;
    padding: 2rem;
    max-width: 500px;
    width: 100%;
    position: relative;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
    animation: modalSlideIn 0.3s ease;
  }

  @keyframes modalSlideIn {
    from {
      opacity: 0;
      transform: translateY(-20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .modal-close {
    position: absolute;
    top: 1rem;
    right: 1rem;
    background: none;
    border: none;
    font-size: 1.5rem;
    cursor: pointer;
    color: #666;
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    transition: all 0.2s ease;
  }

  .modal-close:hover {
    background: #f0f0f0;
    color: #333;
  }

  .modal-title {
    font-family: 'Playfair Display', serif;
    font-size: 1.5rem;
    font-weight: 700;
    color: #1a1a2e;
    margin-bottom: 1.5rem;
    text-align: center;
  }

  .modal-field {
    margin-bottom: 1.25rem;
  }

  .modal-field label {
    display: block;
    font-weight: 600;
    color: #333;
    margin-bottom: 0.5rem;
    font-size: 0.9375rem;
  }

  .modal-select {
    width: 100%;
    padding: 0.75rem 1rem;
    border: 2px solid #e0e0e0;
    border-radius: 0.5rem;
    font-size: 1rem;
    color: #333;
    background: white;
    cursor: pointer;
    transition: border-color 0.2s ease;
  }

  .modal-select:focus {
    outline: none;
    border-color: #17bfcf;
  }

  .modal-stars {
    display: flex;
    gap: 0.5rem;
  }

  .star-btn {
    background: none;
    border: none;
    font-size: 2rem;
    color: #ddd;
    cursor: pointer;
    transition: all 0.2s ease;
    padding: 0;
  }

  .star-btn:hover,
  .star-btn.active {
    color: #ffc107;
    transform: scale(1.1);
  }

  .modal-textarea {
    width: 100%;
    padding: 0.75rem 1rem;
    border: 2px solid #e0e0e0;
    border-radius: 0.5rem;
    font-size: 1rem;
    color: #333;
    resize: vertical;
    min-height: 100px;
    font-family: inherit;
    transition: border-color 0.2s ease;
  }

  .modal-textarea:focus {
    outline: none;
    border-color: #17bfcf;
  }

  .modal-textarea::placeholder {
    color: #999;
  }

  .modal-actions {
    display: flex;
    gap: 1rem;
    margin-top: 1.5rem;
  }

  .modal-submit {
    flex: 1;
    padding: 0.875rem 1.5rem;
    background: linear-gradient(135deg, #17bfcf 0%, #0d9488 100%);
    color: white;
    border: none;
    border-radius: 0.5rem;
    font-weight: 600;
    font-size: 1rem;
    cursor: pointer;
    transition: all 0.3s ease;
  }

  .modal-submit:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 4px 15px rgba(23, 191, 207, 0.4);
  }

  .modal-submit:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }

  .modal-cancel {
    padding: 0.875rem 1.5rem;
    background: #f0f0f0;
    color: #666;
    border: none;
    border-radius: 0.5rem;
    font-weight: 600;
    font-size: 1rem;
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .modal-cancel:hover {
    background: #e0e0e0;
    color: #333;
  }

  @media (max-width: 480px) {
    .review-modal {
      padding: 1.5rem;
    }

    .modal-title {
      font-size: 1.25rem;
    }

    .modal-actions {
      flex-direction: column;
    }
  }

  /* Newsletter Section */
  .newsletter-section {
    width: 100%;
    background: linear-gradient(135deg, #f8fafb 0%, #f0f4f8 100%);
    padding: 3rem 1rem;
  }

  @media (min-width: 640px) {
    .newsletter-section {
      padding: 4rem 1.5rem;
    }
  }

  @media (min-width: 768px) {
    .newsletter-section {
      padding: 5rem 1.5rem;
    }
  }

  @media (min-width: 1024px) {
    .newsletter-section {
      padding: 6rem 1.5rem;
    }
  }

  .newsletter-content {
    max-width: 640px;
    margin: 0 auto;
    text-align: center;
  }

  .newsletter-title {
    font-family: 'Playfair Display', serif;
    font-size: 1.5rem;
    font-weight: 700;
    color: #111718;
    margin: 0 0 0.75rem;
  }

  @media (min-width: 640px) {
    .newsletter-title {
      font-size: 1.875rem;
      margin: 0 0 1rem;
    }
  }

  @media (min-width: 768px) {
    .newsletter-title {
      font-size: 2.25rem;
    }
  }

  .newsletter-text {
    font-size: 0.9375rem;
    color: #6b7280;
    margin: 0 0 1.5rem;
    line-height: 1.6;
  }

  @media (min-width: 768px) {
    .newsletter-text {
      font-size: 1rem;
      margin: 0 0 2rem;
    }
  }

  .newsletter-form {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }

  @media (min-width: 640px) {
    .newsletter-form {
      flex-direction: row;
    }
  }

  .newsletter-input {
    flex: 1;
    height: 48px;
    padding: 0 1rem;
    border: 1px solid #d1d5db;
    border-radius: 0.5rem;
    background: white;
    font-size: 1rem;
    color: #111718;
    transition: all 0.3s ease;
  }

  .newsletter-input:focus {
    outline: none;
    border-color: #17bfcf;
    box-shadow: 0 0 0 3px rgba(23, 191, 207, 0.1);
  }

  .newsletter-input::placeholder {
    color: #9ca3af;
  }

  .newsletter-btn {
    height: 48px;
    min-width: 120px;
    padding: 0 1.25rem;
    background: #111718;
    border: none;
    border-radius: 0.5rem;
    font-size: 0.9375rem;
    font-weight: 700;
    color: white;
    cursor: pointer;
    transition: all 0.3s ease;
  }

  .newsletter-btn:hover {
    background: #17bfcf;
    transform: translateY(-2px);
  }

  .newsletter-btn:active {
    transform: translateY(0);
  }

  @media (min-width: 640px) {
    .newsletter-btn {
      min-width: 140px;
      padding: 0 1.5rem;
      font-size: 1rem;
    }
  }

  /* RTL Support */
  &[dir='rtl'] {
    .hero-content {
      text-align: center;
    }
    
    @media (min-width: 768px) {
      .hero-content {
        text-align: right;
      }
    }
    
    .categories-header,
    .feature-card,
    .newsletter-content {
      text-align: right;
    }

    .category-name {
      left: auto;
      right: 1.5rem;
    }

    .hero-title,
    .categories-title,
    .products-title,
    .feature-title,
    .newsletter-title,
    .reviews-title {
      font-family: 'Arial', 'Tahoma', serif;
    }
    
    @media (max-width: 767px) {
      .feature-card {
        text-align: center;
      }
    }
  }
`;

export default HomePage;
